import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { getMaintenanceStatus } from '@/client/sdk.gen'
import type { MaintenanceStatus } from '@/client/types.gen'

const POLL_INTERVAL_MS = 60_000

const DEFAULT_STATUS: MaintenanceStatus = {
	banner_enabled: false,
	overlay_enabled: false,
	message: '',
}

interface MaintenanceContextType {
	status: MaintenanceStatus
	loading: boolean
	refresh: () => Promise<void>
}

const MaintenanceContext = createContext<MaintenanceContextType | undefined>(undefined)

interface MaintenanceProviderProps {
	children: ReactNode
}

export function MaintenanceProvider({ children }: MaintenanceProviderProps) {
	const [status, setStatus] = useState<MaintenanceStatus>(DEFAULT_STATUS)
	const [loading, setLoading] = useState(true)

	const refresh = useCallback(async () => {
		try {
			const response = await getMaintenanceStatus()
			if (response.data) {
				setStatus(response.data)
			}
		} catch {
			// Fail open: do not block the app when status cannot be loaded.
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		void refresh()
		const intervalId = window.setInterval(() => {
			void refresh()
		}, POLL_INTERVAL_MS)
		return () => window.clearInterval(intervalId)
	}, [refresh])

	const value = useMemo(
		() => ({
			status,
			loading,
			refresh,
		}),
		[status, loading, refresh]
	)

	return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>
}

export function useMaintenance(): MaintenanceContextType {
	const context = useContext(MaintenanceContext)
	if (context === undefined) {
		throw new Error('useMaintenance must be used within a MaintenanceProvider')
	}
	return context
}
